import { KeyboardEvent, ReactNode, useCallback } from 'react';
import { Card } from '@hqo/ui-atoms';
import { cn } from '@hqo/react-hooks';
import { ActivityType } from 'app/data/types/activity';

/*
 * DS `Card` gives bg/foreground; display, padding and radius come from classes. The border, shadow
 * and hover/selected depth live in `index.css` under `.activity-card` — a `box-shadow` with commas
 * inside `rgba()` does not survive Tailwind's arbitrary-value parser (`--tw-shadow` comes out empty
 * and the card renders flat), and an inline style cannot express `:hover`. See that rule for the
 * values and why they are shaped the way they are.
 */
const SHELL_CLASS = 'activity-card block rounded-2xl p-4';

interface CardShellProps {
  tappable: boolean;
  onActivate: () => void;
  testId: string;
  type: ActivityType;
  /**
   * Whether this card is the one open beside the list. Only ever true where the host shows a record
   * without leaving the list — elsewhere the tap navigates away and a highlight would just flash.
   */
  isSelected?: boolean;
  children: ReactNode;
}

/**
 * The card's outer surface (DS `Card`). A tappable card is an accessible `role="button"` (click +
 * Enter/Space); a non-tappable card (no routable action) is a plain surface with no interaction.
 */
export const CardShell = ({ tappable, onActivate, testId, type, isSelected = false, children }: CardShellProps): JSX.Element => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      // Only the shell itself activates on Enter/Space. A keydown that bubbled up from a nested
      // control (Approve estimate, Book again, a rating star) must reach that control's own
      // handler, not open the card generically.
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
    [onActivate],
  );

  if (!tappable) {
    return (
      /*
       * `aria-current` here as well as on the tappable branch: a selected booking can go
       * non-tappable under the reader — a status change, or the feed refreshing — and the record
       * stays open beside it. Losing the attribute would tell assistive tech the selection went
       * away when only the tap did.
       */
      <Card
        className={SHELL_CLASS}
        data-testid={testId}
        data-type={type}
        aria-current={isSelected || undefined}
        data-selected={isSelected || undefined}
      >
        {children}
      </Card>
    );
  }

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={handleKeyDown}
      className={cn(SHELL_CLASS, 'cursor-pointer')}
      data-testid={testId}
      data-type={type}
      // `aria-current`, not `aria-selected`: this is the one of several the reader is looking at,
      // and `aria-selected` is only meaningful inside a listbox/grid, which this is not.
      aria-current={isSelected || undefined}
      data-selected={isSelected || undefined}
    >
      {children}
    </Card>
  );
};
